import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "labelCanvas",
    "canvasWrapper", 
    "elementProperties",
    "elementWidth",
    "elementHeight", 
    "elementX",
    "elementY",
    "elementText",
    "elementCustomText",
    "elementFontSize",
    "elementFontFamily",
    "elementFontWeight",
    "elementFontStyle",
    "elementTextDecoration",
    "textProperties",
    "customTextGroup",
    "labelSizeSelect",
    "customWidthInput",
    "customHeightInput",
    "customDimensionsGroup",
    "customWidthField",
    "customHeightField",
    "previewButton"
  ]

  connect() {
    // Canvas elements management
    this.canvasElements = []
    this.selectedElement = null
    this.dragState = null
    this.resizeState = null
    this.elementIdCounter = 0
    this.previewMode = false
    this.snapToGrid = true
    this.gridSize = 10
    
    // Scale factor: converts mm to display pixels
    // This should match the PDF rendering ratio
    // PDF uses 2.83465 pts/mm, we use 3.78 px/mm for display
    // The conversion factor between display and PDF is ~0.75
    this.mmToPx = 3.78
    
    // Setup canvas event listeners
    this._setupCanvasEventListeners()
    
    // Load existing template data if editing
    this.loadTemplateData()
    
    // Load custom dimensions if they exist
    this.loadCustomDimensions()
    
    // Initial canvas size update
    this.updateCanvasSize()
  }

  disconnect() {
    this._cleanupCanvasEventListeners()
  }

  _setupCanvasEventListeners() {
    this._onMouseMove = this._onMouseMove.bind(this)
    this._onMouseUp = this._onMouseUp.bind(this)
    
    document.addEventListener('mousemove', this._onMouseMove)
    document.addEventListener('mouseup', this._onMouseUp)
  }

  _cleanupCanvasEventListeners() {
    document.removeEventListener('mousemove', this._onMouseMove)
    document.removeEventListener('mouseup', this._onMouseUp)
  }

  loadTemplateData() {
    // Load existing template data from the form
    const elementsInput = document.querySelector('input[name=\"qr_template[elements]\"]')
    if (elementsInput && elementsInput.value) {
      try {
        const elements = JSON.parse(elementsInput.value)
        elements.forEach(elementData => {
          elementData.id = ++this.elementIdCounter
          this._createElement(elementData)
          this.canvasElements.push(elementData)
        })
      } catch (error) {
        console.error('Error loading template data:', error)
      }
    }
  }

  loadCustomDimensions() {
    // Load custom dimensions from hidden fields
    const customWidthField = document.querySelector('input[name="qr_template[custom_width]"]')
    const customHeightField = document.querySelector('input[name="qr_template[custom_height]"]')
    
    if (customWidthField && customWidthField.value && this.hasCustomWidthInputTarget) {
      this.customWidthInputTarget.value = customWidthField.value
    }
    
    if (customHeightField && customHeightField.value && this.hasCustomHeightInputTarget) {
      this.customHeightInputTarget.value = customHeightField.value
    }
  }

  // Canvas Size Management
  updateCanvasSize() {
    if (!this.hasLabelCanvasTarget) return

    const labelSize = this.hasLabelSizeSelectTarget 
      ? this.labelSizeSelectTarget.value 
      : document.querySelector('input[name="qr_template[label_size]"]:checked')?.value || '4x6'
    
    const canvas = this.labelCanvasTarget
    
    let widthMm, heightMm
    
    // All sizes defined in mm for consistency with PDF generation
    const sizes = {
      '4x6': { width: 101.6, height: 152.4 },  // 4"x6" thermal (in mm)
      'a6': { width: 105, height: 148 },        // A6 paper
      'a4_grid': { width: 99, height: 67.7 }    // Single cell of A4 2x4 grid
    }
    
    if (labelSize === 'custom') {
      // Use custom dimensions
      widthMm = this.hasCustomWidthInputTarget ? parseFloat(this.customWidthInputTarget.value) || 100 : 100
      heightMm = this.hasCustomHeightInputTarget ? parseFloat(this.customHeightInputTarget.value) || 100 : 100
      
      // Sync with hidden fields
      if (this.hasCustomWidthFieldTarget) this.customWidthFieldTarget.value = widthMm
      if (this.hasCustomHeightFieldTarget) this.customHeightFieldTarget.value = heightMm
    } else {
      const size = sizes[labelSize] || sizes['4x6']
      widthMm = size.width
      heightMm = size.height
    }
    
    // Convert mm to pixels for display
    const width = widthMm * this.mmToPx
    const height = heightMm * this.mmToPx
    
    // Store actual dimensions in mm for reference
    canvas.dataset.widthMm = widthMm
    canvas.dataset.heightMm = heightMm
    canvas.dataset.actualWidth = width
    canvas.dataset.actualHeight = height
    
    // Set canvas size
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`
    
    // Update custom dimensions visibility
    this._updateCustomDimensionsVisibility(labelSize)
    
    // Clear existing placeholder text if elements exist
    if (this.canvasElements.length === 0) {
      this._showCanvasPlaceholder()
    }
  }

  _showCanvasPlaceholder() {
    if (this.canvasElements.length === 0) {
      this.labelCanvasTarget.innerHTML = '<p style="color: var(--text-muted); margin: 0; text-align: center; line-height: 200px;">Usa los botones de arriba para agregar elementos</p>'
    }
  }

  _updateCustomDimensionsVisibility(labelSize) {
    if (this.hasCustomDimensionsGroupTarget) {
      this.customDimensionsGroupTarget.style.display = labelSize === 'custom' ? 'block' : 'none'
    }
  }

  _clearCanvasPlaceholder() {
    const placeholder = this.labelCanvasTarget.querySelector('p')
    if (placeholder) {
      placeholder.remove()
    }
  }

  // Element Management
  addQrElement() {
    this._clearCanvasPlaceholder()
    
    const element = {
      id: ++this.elementIdCounter,
      type: 'qr',
      x: this._snapToGrid(50),
      y: this._snapToGrid(20),
      width: 100,
      height: 100
    }
    
    this._createElement(element)
    this.canvasElements.push(element)
    this._markAsModified()
  }

  addTextElement(event) {
    this._clearCanvasPlaceholder()
    
    const textType = event.currentTarget.dataset.textType || 'brand'
    
    const element = {
      id: ++this.elementIdCounter,
      type: 'text',
      x: this._snapToGrid(20),
      y: this._snapToGrid(130),
      width: 180,
      height: 25,
      textType: textType,
      fontSize: 14,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textDecoration: 'none',
      customText: ''
    }
    
    this._createElement(element)
    this.canvasElements.push(element)
    this._markAsModified()
  }

  clearCanvas() {
    this.canvasElements.forEach(element => {
      if (element.element) {
        element.element.remove()
      }
    })
    this.canvasElements = []
    this.selectedElement = null
    this._hideElementProperties()
    this._showCanvasPlaceholder()
    this._markAsModified()
  }

  _createElement(elementData) {
    const element = document.createElement('div')
    element.className = `canvas-element ${elementData.type}-element`
    element.dataset.elementId = elementData.id
    
    element.style.left = `${elementData.x}px`
    element.style.top = `${elementData.y}px`
    element.style.width = `${elementData.width}px`
    element.style.height = `${elementData.height}px`
    
    if (elementData.type === 'qr') {
      element.innerHTML = `<svg viewBox="0 0 40 40" style="width: 100%; height: 100%; background: white;">
        <rect width="40" height="40" fill="white"/>
        <rect x="2" y="2" width="12" height="12" fill="black"/>
        <rect x="26" y="2" width="12" height="12" fill="black"/>
        <rect x="2" y="26" width="12" height="12" fill="black"/>
        <rect x="26" y="26" width="12" height="12" fill="black"/>
      </svg>`
    } else {
      // Apply font properties
      element.style.fontSize = `${elementData.fontSize || 14}px`
      element.style.fontFamily = elementData.fontFamily || 'Arial, sans-serif'
      element.style.fontWeight = elementData.fontWeight || 'normal'
      element.style.fontStyle = elementData.fontStyle || 'normal'
      element.style.textDecoration = elementData.textDecoration || 'none'
      
      const displayText = elementData.textType === 'custom' 
        ? (elementData.customText || 'Texto personalizado')
        : this._getFieldDisplayName(elementData.textType)
      element.textContent = displayText
    }
    
    // Add resize handle
    const resizeHandle = document.createElement('div')
    resizeHandle.className = 'resize-handle'
    element.appendChild(resizeHandle)
    
    // Store reference to resize handle in element data
    elementData.resizeHandle = resizeHandle
    
    // Add event listeners (resize handle works even in preview mode)
    element.addEventListener('mousedown', (e) => this._onElementMouseDown(e, elementData))
    resizeHandle.addEventListener('mousedown', (e) => this._onResizeMouseDown(e, elementData))
    
    this.labelCanvasTarget.appendChild(element)
    elementData.element = element
    
    // Auto-select the new element
    this._selectElement(elementData)
  }

  _getTextLabel(textType) {
    return this._getFieldDisplayName(textType)
  }

  // Canvas Interaction Methods
  handleCanvasClick(event) {
    // Deselect if clicking on empty canvas
    if (event.target === this.labelCanvasTarget) {
      this._deselectAllElements()
    }
  }

  _onElementMouseDown(event, elementData) {
    event.stopPropagation()
    event.preventDefault()
    
    this._selectElement(elementData)
    
    const rect = this.labelCanvasTarget.getBoundingClientRect()
    this.dragState = {
      element: elementData,
      startX: event.clientX - rect.left - elementData.x,
      startY: event.clientY - rect.top - elementData.y
    }
  }

  _onResizeMouseDown(event, elementData) {
    event.stopPropagation()
    event.preventDefault()
    
    this.resizeState = {
      element: elementData,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: elementData.width,
      startHeight: elementData.height
    }
  }

  _onMouseMove(event) {
    if (this.dragState) {
      const rect = this.labelCanvasTarget.getBoundingClientRect()
      let x = event.clientX - rect.left - this.dragState.startX
      let y = event.clientY - rect.top - this.dragState.startY
      
      // Apply snap to grid
      x = this._snapToGrid(x)
      y = this._snapToGrid(y)
      
      // Constrain to canvas bounds
      const maxX = rect.width - this.dragState.element.width
      const maxY = rect.height - this.dragState.element.height
      
      this.dragState.element.x = Math.max(0, Math.min(x, maxX))
      this.dragState.element.y = Math.max(0, Math.min(y, maxY))
      
      this._updateElementPosition(this.dragState.element)
      this._markAsModified()
    }
    
    if (this.resizeState) {
      const deltaX = event.clientX - this.resizeState.startX
      const deltaY = event.clientY - this.resizeState.startY
      
      let newWidth = Math.max(20, this.resizeState.startWidth + deltaX)
      let newHeight = Math.max(20, this.resizeState.startHeight + deltaY)
      
      // Mantener proporción 1:1 para elementos QR
      if (this.resizeState.element.type === 'qr') {
        const delta = Math.max(deltaX, deltaY)
        newWidth = Math.max(20, this.resizeState.startWidth + delta)
        newHeight = newWidth // Mantener cuadrado
      }
      
      this.resizeState.element.width = newWidth
      this.resizeState.element.height = newHeight
      
      this._updateElementSize(this.resizeState.element)
      this._markAsModified()
    }
  }

  _onMouseUp(event) {
    this.dragState = null
    this.resizeState = null
  }

  _selectElement(elementData) {
    this._deselectAllElements()
    this.selectedElement = elementData
    elementData.element.classList.add('selected')
    this._showElementProperties(elementData)
  }

  _deselectAllElements() {
    this.canvasElements.forEach(element => {
      element.element.classList.remove('selected')
    })
    this.selectedElement = null
    this._hideElementProperties()
  }

  _updateElementPosition(elementData) {
    elementData.element.style.left = `${elementData.x}px`
    elementData.element.style.top = `${elementData.y}px`
    
    if (elementData === this.selectedElement) {
      this._updatePropertyInputs()
    }
  }

  _updateElementSize(elementData) {
    elementData.element.style.width = `${elementData.width}px`
    elementData.element.style.height = `${elementData.height}px`
    
    if (elementData === this.selectedElement) {
      this._updatePropertyInputs()
    }
  }

  // Element Properties Panel
  _showElementProperties(elementData) {
    if (!this.hasElementPropertiesTarget) return
    
    this.elementPropertiesTarget.style.display = 'block'
    
    // Update input values
    if (this.hasElementWidthTarget) this.elementWidthTarget.value = elementData.width
    if (this.hasElementHeightTarget) this.elementHeightTarget.value = elementData.height
    if (this.hasElementXTarget) this.elementXTarget.value = elementData.x
    if (this.hasElementYTarget) this.elementYTarget.value = elementData.y
    
    // Show/hide text properties
    if (this.hasTextPropertiesTarget) {
      this.textPropertiesTarget.style.display = elementData.type === 'text' ? 'block' : 'none'
      
      if (elementData.type === 'text') {
        if (this.hasElementTextTarget) this.elementTextTarget.value = elementData.textType
        if (this.hasElementFontSizeTarget) this.elementFontSizeTarget.value = elementData.fontSize || 14
        if (this.hasElementFontFamilyTarget) this.elementFontFamilyTarget.value = elementData.fontFamily || 'Arial'
        if (this.hasElementFontWeightTarget) this.elementFontWeightTarget.value = elementData.fontWeight || 'normal'
        if (this.hasElementFontStyleTarget) this.elementFontStyleTarget.value = elementData.fontStyle || 'normal'
        if (this.hasElementTextDecorationTarget) this.elementTextDecorationTarget.value = elementData.textDecoration || 'none'
        if (this.hasElementCustomTextTarget) this.elementCustomTextTarget.value = elementData.customText || ''
        
        this._updateCustomTextVisibility(elementData.textType)
      }
    }
  }

  _hideElementProperties() {
    if (this.hasElementPropertiesTarget) {
      this.elementPropertiesTarget.style.display = 'none'
    }
  }

  _updatePropertyInputs() {
    if (!this.selectedElement) return
    
    if (this.hasElementWidthTarget) this.elementWidthTarget.value = this.selectedElement.width
    if (this.hasElementHeightTarget) this.elementHeightTarget.value = this.selectedElement.height
    if (this.hasElementXTarget) this.elementXTarget.value = this.selectedElement.x
    if (this.hasElementYTarget) this.elementYTarget.value = this.selectedElement.y
  }

  updateSelectedElement() {
    if (!this.selectedElement) return
    
    // Update from property inputs
    if (this.hasElementWidthTarget) {
      this.selectedElement.width = parseInt(this.elementWidthTarget.value) || 20
    }
    if (this.hasElementHeightTarget) {
      this.selectedElement.height = parseInt(this.elementHeightTarget.value) || 20
    }
    if (this.hasElementXTarget) {
      this.selectedElement.x = parseInt(this.elementXTarget.value) || 0
    }
    if (this.hasElementYTarget) {
      this.selectedElement.y = parseInt(this.elementYTarget.value) || 0
    }
    
    // Update text properties
    if (this.selectedElement.type === 'text') {
      if (this.hasElementTextTarget) {
        this.selectedElement.textType = this.elementTextTarget.value
        this._updateCustomTextVisibility(this.selectedElement.textType)
      }
      if (this.hasElementFontSizeTarget) {
        this.selectedElement.fontSize = parseInt(this.elementFontSizeTarget.value) || 12
        this.selectedElement.element.style.fontSize = `${this.selectedElement.fontSize}px`
      }
      if (this.hasElementFontFamilyTarget) {
        this.selectedElement.fontFamily = this.elementFontFamilyTarget.value
        this.selectedElement.element.style.fontFamily = this.selectedElement.fontFamily
      }
      if (this.hasElementFontWeightTarget) {
        this.selectedElement.fontWeight = this.elementFontWeightTarget.value
        this.selectedElement.element.style.fontWeight = this.selectedElement.fontWeight
      }
      if (this.hasElementFontStyleTarget) {
        this.selectedElement.fontStyle = this.elementFontStyleTarget.value
        this.selectedElement.element.style.fontStyle = this.selectedElement.fontStyle
      }
      if (this.hasElementTextDecorationTarget) {
        this.selectedElement.textDecoration = this.elementTextDecorationTarget.value
        this.selectedElement.element.style.textDecoration = this.selectedElement.textDecoration
      }
      if (this.hasElementCustomTextTarget) {
        this.selectedElement.customText = this.elementCustomTextTarget.value
      }
      
      // Update text content (mantener contenido en modo preview)
      if (!this.previewMode) {
        const textContent = this.selectedElement.textType === 'custom' 
          ? this.selectedElement.customText 
          : this._getTextLabel(this.selectedElement.textType)
        
        this._updateElementTextContent(this.selectedElement, textContent)
      }
    }
    
    // Apply visual updates
    this._updateElementPosition(this.selectedElement)
    this._updateElementSize(this.selectedElement)
    this._markAsModified()
  }

  _updateCustomTextVisibility(textType) {
    if (this.hasCustomTextGroupTarget) {
      this.customTextGroupTarget.style.display = textType === 'custom' ? 'block' : 'none'
    }
  }

  _markAsModified() {
    // Update the hidden input with current canvas state
    this._updateFormData()
  }

  _updateFormData() {
    // Update the elements hidden input
    const elementsInput = document.querySelector('input[name="qr_template[elements]"]')
    if (elementsInput) {
      const elementsData = this.canvasElements.map(element => ({
        type: element.type,
        x: element.x,
        y: element.y,
        width: element.width,
        height: element.height,
        textType: element.textType,
        fontSize: element.fontSize,
        fontFamily: element.fontFamily,
        fontWeight: element.fontWeight,
        fontStyle: element.fontStyle,
        textDecoration: element.textDecoration,
        customText: element.customText
      }))
      elementsInput.value = JSON.stringify(elementsData)
    }
  }

  saveTemplate() {
    this._updateFormData()
    
    // Submit the form
    const form = this.element.querySelector('form')
    if (form) {
      form.requestSubmit()
    }
  }

  // Keyboard shortcuts
  handleKeydown(event) {
    if (event.key === 'Delete' && this.selectedElement) {
      this.deleteSelectedElement()
      event.preventDefault()
    }
  }

  deleteSelectedElement() {
    if (!this.selectedElement) return
    
    const index = this.canvasElements.indexOf(this.selectedElement)
    if (index > -1) {
      this.selectedElement.element.remove()
      this.canvasElements.splice(index, 1)
      this.selectedElement = null
      this._hideElementProperties()
      this._markAsModified()
      
      if (this.canvasElements.length === 0) {
        this._showCanvasPlaceholder()
      }
    }
  }

  // Snap position to grid
  _snapToGrid(value) {
    if (!this.snapToGrid) return value
    return Math.round(value / this.gridSize) * this.gridSize
  }

  // Toggle preview mode
  togglePreview() {
    this.previewMode = !this.previewMode
    
    if (this.previewMode) {
      this._showPreview()
      if (this.hasPreviewButtonTarget) {
        this.previewButtonTarget.textContent = 'Editar'
        this.previewButtonTarget.classList.remove('btn-outline-primary')
        this.previewButtonTarget.classList.add('btn-primary')
      }
    } else {
      this._hidePreview()
      if (this.hasPreviewButtonTarget) {
        this.previewButtonTarget.textContent = 'Vista Previa'
        this.previewButtonTarget.classList.remove('btn-primary')
        this.previewButtonTarget.classList.add('btn-outline-primary')
      }
    }
  }

  // Show preview with sample data
  _showPreview() {
    const sampleData = {
      brand: 'Nike',
      model: 'Air Max 90',
      color: 'Negro/Blanco',
      cut: 'Bajo',
      category: 'Deportivos',
      general_category: 'Calzado',
      supplier: 'Distribuidora ABC',
      cost: '$1,200.00',
      price: '$2,500.00',
      size: '8.5',
      qr_code: 'NIK-AM90-BW-85-001'
    }

    this.canvasElements.forEach(element => {
      if (element.type === 'text' && element.element) {
        const textContent = element.textType === 'custom' 
          ? element.customText || 'Texto personalizado'
          : sampleData[element.textType] || element.textType
        
        this._updateElementTextContent(element, textContent)
      }
    })
  }

  // Hide preview and restore field names
  _hidePreview() {
    this.canvasElements.forEach(element => {
      if (element.type === 'text' && element.element) {
        const displayText = element.textType === 'custom' 
          ? (element.customText || 'Texto personalizado')
          : this._getFieldDisplayName(element.textType)
        
        this._updateElementTextContent(element, displayText)
      }
    })
  }

  // Get display name for field types
  _getFieldDisplayName(textType) {
    const fieldNames = {
      brand: 'Marca',
      model: 'Modelo', 
      color: 'Color',
      cut: 'Corte',
      category: 'Categoría',
      general_category: 'Categoría General',
      supplier: 'Proveedor',
      cost: 'Costo',
      price: 'Precio',
      size: 'Talla',
      qr_code: 'Código QR'
    }
    return fieldNames[textType] || textType
  }

  // Helper method to safely update text content while preserving resize handle
  _updateElementTextContent(element, textContent) {
    if (element.type === 'text' && element.element) {
      // Find and preserve the resize handle
      let resizeHandle = element.element.querySelector('.resize-handle')
      
      // If no handle found, use stored reference
      if (!resizeHandle && element.resizeHandle) {
        resizeHandle = element.resizeHandle
      }
      
      // Update text content
      element.element.textContent = textContent
      
      // Re-add the resize handle
      if (resizeHandle) {
        element.element.appendChild(resizeHandle)
      }
    }
  }
}